import { useState, useMemo, useEffect, useRef } from "react";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table";
import { Table } from "reactstrap";
import {
  FaArrowUp,
  FaArrowDown,
  FaChevronRight,
  FaChevronDown,
} from "react-icons/fa6";
import buildTree from "./buildTree";

export default function TreeDataGrid({
  rows,
  columns,
  treeColumnId,
  className,
  rowClassName,
  rowStyle,
  defaultExpanded = {},
  expandCommand,
}) {
  const [sorting, setSorting] = useState([]);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const isFirstCommand = useRef(true);

  useEffect(() => {
    if (isFirstCommand.current) {
      isFirstCommand.current = false;
      return;
    }
    setExpanded(expandCommand?.expand ? true : {});
  }, [expandCommand]);

  const treeData = useMemo(() => buildTree(rows), [rows]);

  const tableColumns = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id ?? col.key,
        accessorKey: col.key,
        header: col.header,
        cell: col.cell ?? ((props) => props.getValue()),
        size: col.width ?? 150,
        enableSorting: col.sortable !== false,
        enableResizing: true,
        sortingFn: col.sortingFn ?? "alphanumeric",
        meta: col.meta,
      })),
    [columns],
  );

  const table = useReactTable({
    data: treeData,
    columns: tableColumns,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getRowId: (row, _index, parent) =>
      parent ? `${parent.id}.${row.id}` : String(row.id),
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    columnResizeMode: "onChange",
  });

  return (
    <div className="card" style={{ overflowX: "auto" }}>
      <Table
        className={`table-striped ${className || ""}`}
        style={{ width: table.getTotalSize(), tableLayout: "fixed" }}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{
                    position: "relative",
                    width: header.getSize(),
                    userSelect: "none",
                    color: header.column.getIsSorted() ? "#20a8d8" : undefined,
                  }}
                  className={
                    header.column.columnDef.meta?.headerClassName || ""
                  }
                >
                  <div
                    className="d-flex align-items-center"
                    style={{
                      cursor: header.column.getCanSort()
                        ? "pointer"
                        : "default",
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getCanSort() && (
                      <span className="ms-1">
                        {
                          { asc: <FaArrowUp />, desc: <FaArrowDown /> }[
                            header.column.getIsSorted()
                          ]
                        }
                      </span>
                    )}
                  </div>
                  {header.column.getCanResize() && (
                    <span
                      className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                    >
                      <div className="divider-line" />
                      <div className="divider-line" />
                    </span>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={rowClassName?.(row) || ""}
              style={rowStyle?.(row)}
            >
              {row.getVisibleCells().map((cell) => {
                const isTreeColumn = cell.column.id === treeColumnId;
                return (
                  <td
                    key={cell.id}
                    style={{ width: cell.column.getSize(), overflow: "hidden" }}
                    className={`align-content-center ${cell.column.columnDef.meta?.cellClassName || ""}`}
                  >
                    {isTreeColumn ? (
                      <div
                        className="d-flex align-items-center"
                        style={{ paddingLeft: `${row.depth * 20}px` }}
                      >
                        {row.getCanExpand() ? (
                          <span
                            onClick={row.getToggleExpandedHandler()}
                            className="cursor-pointer me-1 flex-shrink-0"
                            style={{ width: "16px" }}
                          >
                            {row.getIsExpanded() ? (
                              <FaChevronDown size={12} />
                            ) : (
                              <FaChevronRight size={12} />
                            )}
                          </span>
                        ) : (
                          <span
                            className="me-1 flex-shrink-0"
                            style={{ width: "16px" }}
                          />
                        )}
                        <span className="text-truncate">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </span>
                      </div>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
